import React from 'react';
import {PhasePill} from './PhasePill';
import {useDashboard} from './context';
import {solutionList} from './scoring';

interface SolutionEntry {
  domains: Set<string>;
  jtbds: {name: string; domain: string; area?: string; phase: string}[];
  areas: Set<string>;
}

/** Tools & experiments rolled up across the portfolio. Ported verbatim from the prototype. */
export function SolutionsView(): React.ReactElement {
  const {domains, serviceAreas} = useDashboard();
  const solutionMap: Record<string, SolutionEntry> = {};
  domains.forEach(d => {
    d.jtbds.forEach(j => {
      solutionList(j).forEach(s => {
        if (!solutionMap[s]) solutionMap[s] = {domains: new Set(), jtbds: [], areas: new Set()};
        solutionMap[s].domains.add(d.name);
        solutionMap[s].jtbds.push({name: j.name, domain: d.name, area: d.area, phase: j.phase});
        if (d.area) solutionMap[s].areas.add(d.area);
      });
    });
  });

  const sorted = Object.entries(solutionMap).sort((a, b) => b[1].jtbds.length - a[1].jtbds.length);

  return (
    <div>
      <div style={{marginBottom: 20}}>
        <h2 style={{fontSize: 20, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.02em', marginBottom: 4}}>
          Solutions &amp; Experiments
        </h2>
        <p style={{fontSize: 13, color: '#64748B'}}>Tools and platforms driving AI transformation across the portfolio</p>
      </div>
      <div style={{display: 'grid', gap: 12}}>
        {sorted.map(([name, data]) => {
          const areaColors = [...data.areas].map(a => serviceAreas.find(s => s.id === a)?.color).filter(Boolean) as string[];
          return (
            <div key={name} style={{background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: '14px 18px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                  <div style={{fontWeight: 700, fontSize: 14, color: '#0F172A'}}>{name}</div>
                  <div style={{display: 'flex', gap: 3}}>
                    {areaColors.map((c, i) => (
                      <div key={i} style={{width: 8, height: 8, borderRadius: '50%', background: c}} />
                    ))}
                  </div>
                </div>
                <div style={{display: 'flex', gap: 12, fontSize: 11, color: '#64748B'}}>
                  <span>
                    <b style={{color: '#334155'}}>{data.jtbds.length}</b> JTBDs
                  </span>
                  <span>
                    <b style={{color: '#334155'}}>{data.domains.size}</b> domains
                  </span>
                </div>
              </div>
              <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                {data.jtbds.map((j, i) => {
                  const areaColor = serviceAreas.find(a => a.id === j.area)?.color || '#94A3B8';
                  return (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '3px 8px',
                        background: '#F8FAFC',
                        borderRadius: 6,
                        border: '1px solid #E2E8F0'
                      }}
                    >
                      <div style={{width: 6, height: 6, borderRadius: '50%', background: areaColor, flexShrink: 0}} />
                      <span style={{fontSize: 11, color: '#475569'}}>{j.name}</span>
                      <PhasePill phase={j.phase} small />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
